import { DateTime } from "luxon";
import type {
  BillingItemSegment,
  RestBillingItem,
  RestUpdateBillingItem,
} from "@KassaroAB/shared/types/billingItem";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import type { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import type { AvailableBillingItemId } from "@KassaroAB/shared/types/ids";

export interface BillingItemPeriodSegment {
  startDate: string;
  endDate: string | null;
  quantity: number;
  billingPeriod: BillingPeriod | null;
  paidUntil: string | null;
}

export const dayBefore = (isoDate: string): string =>
  DateTime.fromISO(isoDate).minus({ days: 1 }).toISODate() ?? isoDate;

export const dayAfter = (isoDate: string): string =>
  DateTime.fromISO(isoDate).plus({ days: 1 }).toISODate() ?? isoDate;

export const usesQuantitySegments = (
  billingItem: Pick<RestBillingItem, "type">
): boolean =>
  billingItem.type === BillingItemType.EXTRA_DEVICE ||
  billingItem.type === BillingItemType.PAYMENT_METHOD;

export const getFirstSegmentStartDate = (
  billingItem: Pick<RestBillingItem, "segments" | "type">
): string | undefined => {
  if (!usesQuantitySegments(billingItem)) {
    return undefined;
  }
  return [...(billingItem.segments ?? [])].sort((first, second) =>
    first.startDate.localeCompare(second.startDate)
  )[0]?.startDate;
};

export const applyStartDateToFirstSegment = (
  segments: BillingItemSegment[] | undefined,
  startDate: string
): BillingItemSegment[] | undefined => {
  if (!segments || segments.length === 0) {
    return undefined;
  }
  const ordered = [...segments].sort((first, second) =>
    first.startDate.localeCompare(second.startDate)
  );
  const [first, ...rest] = ordered;
  if (!first) {
    return undefined;
  }
  return [{ ...first, startDate }, ...rest];
};

export const applyEndDateToSegments = (
  segments: BillingItemSegment[] | undefined,
  endDate: string | null
): BillingItemSegment[] | undefined => {
  if (!segments || segments.length === 0) {
    return undefined;
  }
  const ordered = [...segments].sort((first, second) =>
    first.startDate.localeCompare(second.startDate)
  );
  const last = ordered[ordered.length - 1];
  if (!last) {
    return ordered;
  }

  const kept = last.quantity === 0 ? ordered.slice(0, -1) : ordered;
  const lastActive = kept[kept.length - 1];
  if (!lastActive) {
    return ordered;
  }

  if (endDate === null) {
    return kept;
  }

  const endSegmentStart = dayAfter(endDate);
  if (endSegmentStart <= lastActive.startDate) {
    return ordered;
  }

  const endSegment =
    last.quantity === 0
      ? { ...last, startDate: endSegmentStart }
      : {
          startDate: endSegmentStart,
          quantity: 0,
          billingPeriod: null,
          discount: null,
          paidUntil: null,
        };

  return [...kept, endSegment];
};

export const itemDatesFromSegments = (
  segments: BillingItemSegment[]
): { startDate: string; endDate: string | null } | undefined => {
  const ordered = [...segments].sort((first, second) =>
    first.startDate.localeCompare(second.startDate)
  );
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  if (!first) {
    return undefined;
  }
  return {
    startDate: first.startDate,
    endDate: last && last.quantity === 0 ? dayBefore(last.startDate) : null,
  };
};

export const withSyncedSegmentDates = (
  billingItem: Pick<RestBillingItem, "segments" | "type" | "endDate" | "startDate">,
  update: RestUpdateBillingItem
): RestUpdateBillingItem => {
  if (!usesQuantitySegments(billingItem)) {
    if (update.segments === undefined) {
      return update;
    }
    const rest = { ...update };
    delete rest.segments;
    return rest;
  }

  if (
    update.segments === undefined &&
    update.startDate === undefined &&
    update.endDate === undefined
  ) {
    return update;
  }

  let segments = update.segments ?? billingItem.segments;
  if (!segments || segments.length === 0) {
    return update;
  }

  if (update.segments === undefined) {
    if (update.startDate !== undefined) {
      segments =
        applyStartDateToFirstSegment(segments, update.startDate) ?? segments;
    }
    const endDate =
      update.endDate !== undefined ? update.endDate : billingItem.endDate;
    segments = applyEndDateToSegments(segments, endDate ?? null) ?? segments;
  } else if (update.endDate !== undefined) {
    const last = [...segments].sort((first, second) =>
      first.startDate.localeCompare(second.startDate)
    )[segments.length - 1];
    if (
      last &&
      last.quantity > 0 &&
      (update.endDate === null || last.startDate <= update.endDate)
    ) {
      segments = applyEndDateToSegments(segments, update.endDate) ?? segments;
    }
  }

  const dates = itemDatesFromSegments(segments);
  if (!dates) {
    return update;
  }
  return {
    ...update,
    segments,
    startDate: dates.startDate,
    endDate: dates.endDate,
  };
};

export const getBillingItemPeriodSegments = (
  billingItem: RestBillingItem
): BillingItemPeriodSegment[] => {
  const segments = billingItem.segments;

  if (
    !usesQuantitySegments(billingItem) ||
    !segments ||
    segments.length === 0
  ) {
    return [
      {
        startDate: billingItem.startDate,
        endDate: billingItem.endDate,
        quantity: 1,
        billingPeriod: billingItem.billingPeriod ?? null,
        paidUntil: billingItem.paidUntil,
      },
    ];
  }

  return segments.map((segment, index) => {
    const nextSegment = segments[index + 1];
    const periodEndDate = nextSegment
      ? dayBefore(nextSegment.startDate)
      : billingItem.endDate;
    return {
      startDate: segment.startDate,
      endDate:
        periodEndDate !== null && periodEndDate < segment.startDate
          ? null
          : periodEndDate,
      quantity: segment.quantity,
      billingPeriod: segment.billingPeriod ?? billingItem.billingPeriod ?? null,
      paidUntil: segment.paidUntil,
    };
  });
};

const isSegmentActiveOn = (
  segment: BillingItemPeriodSegment,
  date: string
): boolean =>
  segment.startDate <= date &&
  (segment.endDate === null || segment.endDate >= date);

export const getQuantityOnDate = (
  billingItems: RestBillingItem[],
  availableBillingItemId: AvailableBillingItemId,
  date: string
): number =>
  billingItems
    .filter(
      (billingItem) =>
        billingItem.availableBillingItemId === availableBillingItemId
    )
    .flatMap(getBillingItemPeriodSegments)
    .filter((segment) => isSegmentActiveOn(segment, date))
    .reduce((total, segment) => total + segment.quantity, 0);

export const getQuantityTimeline = (
  billingItems: RestBillingItem[],
  availableBillingItemId: AvailableBillingItemId
): { startDate: string; quantity: number }[] => {
  const segments = billingItems
    .filter(
      (billingItem) =>
        billingItem.availableBillingItemId === availableBillingItemId
    )
    .flatMap(getBillingItemPeriodSegments);

  const changeDates = Array.from(
    new Set(segments.map((segment) => segment.startDate))
  ).sort();

  return changeDates
    .map((startDate) => ({
      startDate,
      quantity: segments
        .filter((segment) => isSegmentActiveOn(segment, startDate))
        .reduce((total, segment) => total + segment.quantity, 0),
    }))
    .filter(
      (entry, index, entries) =>
        index === 0 || entries[index - 1]?.quantity !== entry.quantity
    );
};

export const getActiveSinceOnDate = (
  billingItem: RestBillingItem,
  date: string,
): string => {
  const activeSegment = getBillingItemPeriodSegments(billingItem)
    .filter((segment) => segment.quantity > 0 && segment.startDate <= date)
    .sort((first, second) => second.startDate.localeCompare(first.startDate))[0];
  return activeSegment?.startDate ?? billingItem.startDate;
};

export const findActivePackageItem = (
  billingItems: RestBillingItem[] | undefined,
  date: string
): RestBillingItem | null =>
  (billingItems ?? []).find(
    (billingItem) =>
      billingItem.type === BillingItemType.PACKAGE &&
      getQuantityOnDate(
        [billingItem],
        billingItem.availableBillingItemId,
        date
      ) > 0
  ) ?? null;

export const isBillingItemCurrentOrFuture = (
  billingItem: RestBillingItem,
  date: string
): boolean =>
  getBillingItemPeriodSegments(billingItem).some(
    (segment) =>
      segment.quantity > 0 &&
      (segment.endDate === null || segment.endDate >= date)
  );

export const getCurrentOrNextPositiveSegment = (
  billingItem: RestBillingItem,
  date: string
): BillingItemPeriodSegment | undefined => {
  const segments = getBillingItemPeriodSegments(billingItem);
  return (
    segments.find(
      (segment) =>
        segment.quantity > 0 &&
        isSegmentActiveOn(segment, date)
    ) ??
    segments.find(
      (segment) => segment.quantity > 0 && segment.startDate > date
    ) ??
    segments[0]
  );
};
